import React, { useState, useEffect } from 'react';
import Modal from '../Modal';
import Button from '../Button';
import { PlusCircle, Layout, Check, Search } from 'lucide-react';

const AddToDashboardModal = ({
  isOpen,
  onClose,
  dashboards,
  onAddToDashboard,
  onCreateNewDashboard,
  onDashboardUpdate,
  isQueryJustAdded,
  fetchDashboards
}) => {
  const [newDashboardName, setNewDashboardName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchDashboards();
    } else {
      setNewDashboardName('');
      setSearchTerm('');
    }
  }, [isOpen, fetchDashboards]);

  const filteredDashboards = dashboards.filter(dashboard =>
    dashboard.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateNewDashboard = async () => {
    if (!newDashboardName.trim()) return;
    setIsLoading(true);
    try {
      const newDashboard = await onCreateNewDashboard(newDashboardName);
      if (newDashboard && onDashboardUpdate) {
        onDashboardUpdate(newDashboard);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToDashboard = async (dashboardId) => {
    setIsLoading(true);
    try {
      await onAddToDashboard(dashboardId);
      if (onDashboardUpdate) {
        const updatedDashboard = dashboards.find(d => d.id === dashboardId);
        if (updatedDashboard) {
          onDashboardUpdate(updatedDashboard);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setNewDashboardName('');
    setSearchTerm('');
    onClose();
  };

  const dashboardListMaxHeight = '14rem'; 

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isQueryJustAdded ? "Success!" : ""}
      showButtons={false}
      size="2xl"
    >
      {!isQueryJustAdded ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          {/* Left Side: Existing Dashboards */}
          <div className="flex flex-col space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <Layout className="w-6 h-6 mr-3 text-primary" />
              Add to an Existing Dashboard
            </h3>
            
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search dashboards..."
                className="w-full pl-11 pr-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div 
              className="border border-gray-200 rounded-lg overflow-hidden flex-grow"
              style={{ maxHeight: dashboardListMaxHeight }}
            >
              <div className="overflow-y-auto h-full">
                {filteredDashboards.length > 0 ? (
                  <div className="divide-y divide-gray-200">
                    {filteredDashboards.map((dashboard) => (
                      <button
                        key={dashboard.id}
                        onClick={() => handleAddToDashboard(dashboard.id)}
                        disabled={isLoading}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors duration-150 flex items-center justify-between group disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="text-gray-900 group-hover:text-primary">
                          {dashboard.name}
                        </span>
                        <span className="text-xs text-gray-500">
                          {dashboard.queries?.length || 0} queries
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-8 text-center text-gray-500">
                    No dashboards found.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Side: Create New Dashboard */}
          <div className="flex flex-col space-y-4 border-t md:border-t-0 md:border-l border-gray-200 pt-6 md:pt-0 md:pl-8">
             <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <PlusCircle className="w-6 h-6 mr-3 text-primary"/>
                Or Create a New One
            </h3>
            
            <div className="space-y-4">
                <input
                  id="dashboard-name"
                  type="text"
                  value={newDashboardName}
                  onChange={(e) => setNewDashboardName(e.target.value)}
                  placeholder="Enter a new dashboard name..."
                  // Changed back to rounded-lg for a "squary" look as requested
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  onKeyPress={(e) => {
                      if (e.key === 'Enter' && newDashboardName.trim()) {
                      handleCreateNewDashboard();
                      }
                  }}
                />
              
                <Button
                  onClick={handleCreateNewDashboard}
                  disabled={!newDashboardName.trim() || isLoading}
                  loading={isLoading}
                  className="w-full bg-primary text-white hover:bg-primary/90 flex items-center justify-center py-3 rounded-lg"
                >
                  Create and Add
                </Button>
            </div>
          </div>
        </div>
      ) : (
        // The success message retains its own centered layout and title
        <div className="py-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <p className="text-gray-600 mb-6">
            Your query has been added to the dashboard.
          </p>
          <Button
            onClick={handleClose}
            className="w-full bg-primary text-white hover:bg-primary/90"
          >
            Done
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default AddToDashboardModal;